import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useCart } from "../context/CartContext";

const WORKER_URL =
  "https://afyaequip-payment-worker.leokadiafidelis031.workers.dev";

const PaymentSuccess = () => {
  const [searchParams] = useSearchParams();

  const [status, setStatus] = useState("verifying");
  const [message, setMessage] = useState("We're confirming your payment...");

  const verificationStarted = useRef(false);

  const { clearCart } = useCart();

  useEffect(() => {
    if (verificationStarted.current) {
      return;
    }

    verificationStarted.current = true;

    const verifyPayment = async () => {
      const trackingIdFromUrl =
        searchParams.get("OrderTrackingId") ||
        searchParams.get("orderTrackingId") ||
        searchParams.get("order_tracking_id");

      const trackingIdFromStorage = sessionStorage.getItem(
        "pesapal_tracking_id",
      );

      const trackingId = trackingIdFromUrl || trackingIdFromStorage;

      console.log("Pesapal tracking ID:", trackingId);

      if (!trackingId) {
        setStatus("failed");

        setMessage(
          "We could not find your Pesapal payment information. Please contact AfyaEquip if you were charged.",
        );

        return;
      }

      try {
        const response = await fetch(
          `${WORKER_URL}/check-payment?tracking_id=${encodeURIComponent(
            trackingId,
          )}`,
        );

        const result = await response.json();

        console.log("Pesapal verification response:", result);

        if (!response.ok || !result.success) {
          setStatus("failed");

          setMessage(
            result.message || "We could not verify your payment at this time.",
          );

          return;
        }

        const pesapalResponse = result.pesapal_response || {};

        console.log("Pesapal transaction status:", pesapalResponse);

        const paymentStatus = String(
          pesapalResponse.payment_status_description ||
            pesapalResponse.status ||
            "",
        ).toUpperCase();

        if (paymentStatus === "COMPLETED") {
          const processResponse = await fetch(`${WORKER_URL}/process-order`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              tracking_id: trackingId,
            }),
          });

          const processResult = await processResponse.json();

          if (!processResponse.ok || !processResult.success) {
            setStatus("failed");
            setMessage(
              processResult.message ||
                "Payment was completed, but we could not process your order. Please contact AfyaEquip.",
            );
            return;
          }

          clearCart();
          setStatus("success");
          setMessage(
            "Your payment has been successfully received. Your order has been confirmed and we will contact you shortly regarding delivery.",
          );
          return;
        }

        if (paymentStatus === "PENDING") {
          setStatus("failed");

          setMessage(
            "Your payment is still being processed by Pesapal. Please wait a moment and check your payment status again.",
          );

          return;
        }

        setStatus("failed");

        setMessage(
          pesapalResponse.payment_status_description
            ? `Payment status: ${pesapalResponse.payment_status_description}.`
            : "Payment could not be confirmed.",
        );
      } catch (error) {
        console.error("Pesapal payment verification error:", error);

        setStatus("failed");

        setMessage(
          "We could not verify your payment. If money was deducted from your account, please contact AfyaEquip.",
        );
      }
    };

    verifyPayment();
  }, [searchParams, clearCart]);

  if (status === "verifying") {
    return (
      <main
        className="
          min-h-screen
          bg-gray-50
          flex
          items-center
          justify-center
          px-6
        "
      >
        <div
          className="
            bg-white
            rounded-3xl
            p-10
            text-center
            shadow-sm
            border
            border-gray-200
            max-w-md
            w-full
          "
        >
          <div
            className="
              w-16
              h-16
              mx-auto
              border-4
              border-green-200
              border-t-green-600
              rounded-full
              animate-spin
            "
          />

          <h1
            className="
              text-2xl
              font-bold
              mt-6
            "
          >
            Confirming Payment
          </h1>

          <p className="mt-3 text-gray-600">
            Please wait while we verify your Pesapal payment.
          </p>
        </div>
      </main>
    );
  }

  // =========================================================
  // SUCCESS
  // =========================================================

  if (status === "success") {
    return (
      <main
        className="
          min-h-screen
          bg-gray-50
          flex
          items-center
          justify-center
          px-6
        "
      >
        <div
          className="
            bg-white
            rounded-3xl
            p-10
            text-center
            shadow-lg
            border
            border-gray-200
            max-w-md
            w-full
          "
        >
          <div
            className="
              w-20
              h-20
              mx-auto
              rounded-full
              bg-green-100
              flex
              items-center
              justify-center
              text-green-600
              text-4xl
            "
          >
            ✓
          </div>

          <h1
            className="
              text-3xl
              font-bold
              text-gray-900
              mt-6
            "
          >
            Payment Successful!
          </h1>

          <p className="mt-4 text-gray-600">{message}</p>

          <Link
            to="/shop"
            className="
              block
              mt-8
              w-full
              bg-green-600
              text-white
              py-3
              rounded-xl
              font-semibold
              hover:bg-green-700
              transition
            "
          >
            Continue Shopping
          </Link>
        </div>
      </main>
    );
  }

  // =========================================================
  // FAILED
  // =========================================================

  return (
    <main
      className="
        min-h-screen
        bg-gray-50
        flex
        items-center
        justify-center
        px-6
      "
    >
      <div
        className="
          bg-white
          rounded-3xl
          p-10
          text-center
          shadow-lg
          border
          border-gray-200
          max-w-md
          w-full
        "
      >
        <div
          className="
            w-20
            h-20
            mx-auto
            rounded-full
            bg-red-100
            flex
            items-center
            justify-center
            text-red-600
            text-4xl
          "
        >
          !
        </div>

        <h1
          className="
            text-2xl
            font-bold
            mt-6
          "
        >
          Payment Not Confirmed
        </h1>

        <p className="mt-4 text-gray-600">{message}</p>

        <Link
          to="/checkout"
          className="
            block
            mt-8
            w-full
            bg-green-600
            text-white
            py-3
            rounded-xl
            font-semibold
            hover:bg-green-700
            transition
          "
        >
          Return to Checkout
        </Link>
      </div>
    </main>
  );
};

export default PaymentSuccess;
